/* eslint-disable @typescript-eslint/no-explicit-any */
import { map, Observable } from 'rxjs';
import { DialogService } from 'nebula-ui-kit';
import { inject, Injectable } from '@angular/core';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';
import { CuentaContableFilters, PlanCuenta } from '../../domain/plan-cuentas-modal.models';
import { PlanCuentasService } from './accounting-plan.service';
import { TranslationService } from '@nebula-shared/data-access';

@Injectable({ providedIn: 'root' })
export class CuentaContableModalService {
  readonly dialogService = inject(DialogService);
  readonly planCuentasService = inject(PlanCuentasService);
  readonly translation = inject(TranslationService);

  protected t(key: string, params?: any): string {
    return this.translation.translate(key, params);
  }

  /**
   * @deprecated Abre el selector viejo (SelectDialogComponent). Usa
   * {@link BuscadorMaestroService.abrir} de `@nebula-shared/ui`, la ventana de seleccion del
   * Manual 3.0 (tipo A, selector de catalogo:
   * http://10.120.0.2:8552/propuesto/dist/components/modales.html), con el page-response que
   * este selector ya consulta (`PlanCuentasService.getPaginatedPlanCuentas`). Se retira cuando no quede quien lo llame.
   */
  openSelector(
    title: string,
    currentValue?: string,
    filters?: CuentaContableFilters & { tipoPlanContableId?: number; tipoPlanCodigo?: number },
  ): Observable<PlanCuenta | null> {
    const columns = [
      {
        key: 'codigo',
        label: this.t('TREASURY.COMMON.MODALS.ACCOUNT.COLUMNS.CODE'),
        sortable: true,
        width: '30%',
      },
      {
        key: 'nombre',
        label: this.t('TREASURY.COMMON.MODALS.ACCOUNT.COLUMNS.NAME'),
        sortable: true,
        width: '70%',
      },
    ];

    const loadDataFn = async (params: any): Promise<{ data: PlanCuenta[]; total: number }> => {
      const page = params.page - 1;
      const size = params.limit || 10;
      const searchTerm = params.search || '';

      const adicionales = {
        soloMovimiento: filters?.soloMovimiento,
        soloOrdenDebito: filters?.adicionales?.soloOrdenDebito,
        soloOrdenCredito: filters?.adicionales?.soloOrdenCredito,
        soloIva: filters?.soloIva,
        soloRetencion: filters?.soloRetencion,
        soloAnticipo: filters?.soloAnticipo,
        soloDeduccion: filters?.adicionales?.soloDeduccion,
        tipoPlanCodigo: filters?.tipoPlanCodigo,
        tipoPlanContableId: filters?.tipoPlanContableId,
        diferido: filters?.diferido,
      };

      const naturaleza = filters?.naturaleza
        ? filters.naturaleza === 'DEBITO'
          ? 'Debito'
          : 'Credito'
        : undefined;

      const response = await this.planCuentasService
        .getPaginatedPlanCuentas(
          page,
          size,
          searchTerm,
          params.sortColumn || 'codigo',
          params.sortDirection || 'ASC',
          naturaleza,
          adicionales,
        )
        .toPromise();

      return {
        data: response?.content || [],
        total: response?.totalElements || 0,
      };
    };

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: columns,
        multiSelect: false,
        searchPlaceholder: this.t('TREASURY.COMMON.MODALS.SEARCH.ACCOUNT.PLACEHOLDER'),
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        displayKey: 'codigo',
        selectedItems: currentValue ? [{ codigo: currentValue, nombre: currentValue }] : [],
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
      },
      size: 'lg',
      width: '700px',
    });

    return dialogRef
      .afterClosed()
      .pipe(
        map((selected: PlanCuenta[]) => (selected && selected.length > 0 ? selected[0] : null)),
      );
  }

  openSelectorAccount(title: string, currentValue?: string): Observable<PlanCuenta | null> {
    return this.openSelector(title, currentValue);
  }

  /**
   * @deprecated Abre el selector viejo (SelectDialogComponent). Usa
   * {@link BuscadorMaestroService.abrir} de `@nebula-shared/ui`, la ventana de seleccion del
   * Manual 3.0 (tipo A, selector de catalogo:
   * http://10.120.0.2:8552/propuesto/dist/components/modales.html), con el page-response que
   * este selector ya consulta (`PlanCuentasService.planCuentasporTipo`). Se retira cuando no quede quien lo llame.
   */
  openSelectorTypeDeduction(
    title: string,
    currentValue?: string,
    typeDeduction = 'OD',
  ): Observable<PlanCuenta | null> {
    const columns = [
      {
        key: 'codigo',
        label: this.t('TREASURY.COMMON.MODALS.ACCOUNT.COLUMNS.CODE'),
        sortable: true,
        width: '30%',
      },
      {
        key: 'nombre',
        label: this.t('TREASURY.COMMON.MODALS.ACCOUNT.COLUMNS.NAME'),
        sortable: true,
        width: '70%',
      },
    ];

    const loadDataFn = async (params: any): Promise<{ data: PlanCuenta[]; total: number }> => {
      const page = params.page - 1;
      const size = params.limit || 10;
      const searchTerm = params.search || '';

      const response = await this.planCuentasService
        .planCuentasporTipo(
          params.sortColumn || 'codigo',
          typeDeduction,
          page,
          size,
          searchTerm,
          params.sortDirection || 'ASC',
        )
        .toPromise();

      return {
        data: response?.content || [],
        total: response?.totalElements || 0,
      };
    };

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: columns,
        multiSelect: false,
        searchPlaceholder: this.t('TREASURY.COMMON.MODALS.SEARCH.ACCOUNT.PLACEHOLDER'),
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        displayKey: 'codigo',
        selectedItems: currentValue ? [{ codigo: currentValue, nombre: currentValue }] : [],
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
      },
      size: 'lg',
      width: '700px',
    });

    return dialogRef
      .afterClosed()
      .pipe(
        map((selected: PlanCuenta[]) => (selected && selected.length > 0 ? selected[0] : null)),
      );
  }
}
